// Geo utility functions for deviation detection and distance calculations

interface LatLngLiteral {
  lat: number;
  lng: number;
}

/** Configurable threshold for off-route detection (meters) */
export const DEVIATION_THRESHOLD_METERS = 100;

/** Haversine distance between two points in meters */
export function haversineDistance(a: LatLngLiteral, b: LatLngLiteral): number {
  const R = 6371000; // Earth radius in meters
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);

  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);

  const h = sinDLat * sinDLat + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinDLng * sinDLng;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/**
 * Find the closest point on a polyline to a given point.
 * Iterates each segment, projects the point onto the segment,
 * and returns the minimum distance and nearest point.
 */
export function pointToPolylineDistance(
  point: LatLngLiteral,
  polyline: LatLngLiteral[]
): { distance: number; nearest: LatLngLiteral } {
  let minDist = Infinity;
  let nearest: LatLngLiteral = polyline[0];

  for (let i = 0; i < polyline.length - 1; i++) {
    const a = polyline[i];
    const b = polyline[i + 1];

    // Project point onto segment a->b
    const dx = b.lng - a.lng;
    const dy = b.lat - a.lat;
    const lenSq = dx * dx + dy * dy;

    let t = 0;
    if (lenSq > 0) {
      t = ((point.lng - a.lng) * dx + (point.lat - a.lat) * dy) / lenSq;
      t = Math.max(0, Math.min(1, t));
    }

    const proj: LatLngLiteral = {
      lat: a.lat + t * dy,
      lng: a.lng + t * dx,
    };

    const dist = haversineDistance(point, proj);
    if (dist < minDist) {
      minDist = dist;
      nearest = proj;
    }
  }

  return { distance: minDist, nearest };
}
